import { useComputerCommandMenuState } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useComputerCommandMenuState.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useCallback, useMemo } from 'react';
/**
 * Centralizes the Computer command-menu glue that AskInput needs around the core input hooks.
 *
 * The underlying state hook owns slash-query parsing, active-menu coordination, and controlled
 * open requests. This wrapper adapts those concerns to the generic AskInput callbacks so
 * autosuggestions and typeahead keep their existing behavior while Computer can claim `/`.
 */
export function useComputerCommandMenuIntegration({ showSkills, selectedSearchMode, searchModeOverride, controlledSearchQuery, inputCommandQuery, open: requestedOpen, onOpenChangeRequested, onInsertInputCommandQuery, onCommitInputCommandQuery, onMenuOpen, onInputValueChange, onUpdateAutosuggestions, autosuggestionsEnabled, onTriggerTypeahead, onMentionSearchQueryChange, shouldSuppressTypeaheadTrigger, }) {
    const { open, searchQuery, searchModeOverride: effectiveSearchModeOverride, canUseComputerCommandMenu, onInputChange: onCommandMenuInputChange, onOpenChange, onTypeaheadTrigger: onCommandMenuTypeaheadTrigger, onTypeaheadSearchQueryChange: onCommandMenuTypeaheadSearchQueryChange, shouldSuppressAutosuggestions, } = useComputerCommandMenuState({
        showSkills,
        isComputerMode: (searchModeOverride ?? selectedSearchMode) === SearchMode.ASI,
        searchModeOverride,
        controlledSearchQuery,
        inputCommandQuery,
        open: requestedOpen,
        onOpenChangeRequested,
        onInputValueChange,
        onInsertInputCommandQuery,
        onCommitInputCommandQuery,
        onMenuOpen,
    });
    const handleUpdateAutosuggestions = useCallback((query) => {
        if (shouldSuppressAutosuggestions(query))
            return;
        onUpdateAutosuggestions(query);
    }, [onUpdateAutosuggestions, shouldSuppressAutosuggestions]);
    const onTypeaheadTrigger = useCallback((trigger, query) => {
        if (shouldSuppressTypeaheadTrigger?.(trigger, query))
            return false;
        if (!onCommandMenuTypeaheadTrigger(trigger))
            return false;
        return onTriggerTypeahead?.(trigger, query) ?? true;
    }, [onCommandMenuTypeaheadTrigger, onTriggerTypeahead, shouldSuppressTypeaheadTrigger]);
    const onTypeaheadSearchQueryChange = useCallback((query) => {
        onMentionSearchQueryChange?.(query);
        onCommandMenuTypeaheadSearchQueryChange(query);
    }, [onCommandMenuTypeaheadSearchQueryChange, onMentionSearchQueryChange]);
    return useMemo(() => ({
        open,
        searchQuery,
        searchModeOverride: effectiveSearchModeOverride,
        canUseComputerCommandMenu,
        onInputChange: onCommandMenuInputChange,
        onOpenChange,
        handleUpdateAutosuggestions,
        autosuggestionsEnabled: autosuggestionsEnabled && !open,
        onTypeaheadTrigger,
        onTypeaheadSearchQueryChange,
    }), [
        autosuggestionsEnabled,
        canUseComputerCommandMenu,
        effectiveSearchModeOverride,
        handleUpdateAutosuggestions,
        onCommandMenuInputChange,
        onOpenChange,
        onTypeaheadSearchQueryChange,
        onTypeaheadTrigger,
        open,
        searchQuery,
    ]);
}
//# sourceMappingURL=useComputerCommandMenuIntegration.js.map